import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { HelperService } from '@awo-care/utils';

const NAVBAR_EXPANDED_KEY = 'isExpanded';
const MOBILE_BREAKPOINT = '(max-width: 768px)';

export interface Breadcrumb {
  label: string;
  url: string;
}

@Injectable({
  providedIn: 'root'
})
export class NavbarService {

  router = inject(Router);
  route = inject(ActivatedRoute);
  helperService = inject(HelperService);
  private destroyRef = inject(DestroyRef);

  isMobile = signal(false);
  sidenavMode = computed(() => this.isMobile() ? 'over' as const : 'side' as const);
  sidenavOpened = computed(() => this.isExpanded());

  isExpanded = signal(this.helperService.getStorage<boolean>('local', NAVBAR_EXPANDED_KEY) ?? true);
  navbarTitle = signal('');
  breadcrumbs = signal<Breadcrumb[]>([]);

  toggleSidenav() {
    if (this.isMobile()) {
      this.isExpanded.update(v => !v);
    } else {
      const newValue = !this.isExpanded();
      this.isExpanded.set(newValue);
      this.helperService.setStorage('local', NAVBAR_EXPANDED_KEY, newValue);
    }
  }

  closeSidenavOnMobile() {
    if (this.isMobile()) {
      this.isExpanded.set(false);
    }
  }

  expandSidenav() {
    this.isExpanded.set(true);
    this.helperService.setStorage('local', NAVBAR_EXPANDED_KEY, true);
  }

  collapseSidenav() {
    this.isExpanded.set(false);
    this.helperService.setStorage('local', NAVBAR_EXPANDED_KEY, false);
  }

  initMobileDetection() {
    const mql = window.matchMedia(MOBILE_BREAKPOINT);
    this.isMobile.set(mql.matches);
    if (mql.matches) {
      this.isExpanded.set(false);
    }

    const handler = (e: MediaQueryListEvent) => {
      this.isMobile.set(e.matches);
      if (e.matches) {
        this.isExpanded.set(false);
      }
    };
    mql.addEventListener('change', handler);
    this.destroyRef.onDestroy(() => mql.removeEventListener('change', handler));
  }

  initializeNavbarService() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => {
        const breadcrumbs: Breadcrumb[] = [];
        let currentRoute: ActivatedRoute | null = this.route;
        let url = '';

        while (currentRoute) {
          const segments = currentRoute.snapshot.url;
          if (segments.length) {
            url += '/' + segments.map(s => s.path).join('/');
          }
          const title = currentRoute.snapshot.data['title'];
          const lastLabel = breadcrumbs.length ? breadcrumbs[breadcrumbs.length - 1].label : '';
          if (title && title !== lastLabel) {
            breadcrumbs.push({ label: title, url });
          }
          currentRoute = currentRoute.firstChild;
        }

        return breadcrumbs;
      })
    ).subscribe(breadcrumbs => {
      this.breadcrumbs.set(breadcrumbs);
      this.navbarTitle.set(breadcrumbs.length ? breadcrumbs[breadcrumbs.length - 1].label : '');
    });
  }

  setNavbarTitle(title: string) {
    this.navbarTitle.set(title);
  }
}
